import {
  PropsWithChildren,
  useLayoutEffect,
  useMemo,
  useState,
  useCallback
} from 'react'

import {
  BorderRadius8,
  GreyWhite,
  IndigoIndigo50,
  ShadowM,
  Box,
  Flex
} from '@sequencehq/core-components'
import { useAtomValue } from 'jotai'
import { desiredPositionRefAtom } from '../atoms'
import useMeasure from 'react-use-measure'
import { CONTAINER_STYLES } from 'Invoices/constants'

const VIEWPORT_PADDING = 20

export const Container = ({ children }: PropsWithChildren) => {
  const desiredPositionRef = useAtomValue(desiredPositionRefAtom)
  const [desiredTop, setDesiredTop] = useState<number | null>(null)

  // We measure the container that the editor will be inserted into
  // to calculate where to position it
  const [ref, bounds] = useMeasure({ debounce: 20 })

  // We use position: fixed to position the editor, but we need to know
  // the available space of it's (non-fixed) parent to calculate editor width
  const [spaceRef, spaceBounds] = useMeasure()
  const viewportHeight = useViewportHeight()

  // Nudge the position of the editor so the name field aligns with the name cell in the table,
  // or the 'add line item' button
  const topOffset = -73

  const updateDesiredPositionFromRef = useCallback(() => {
    if (!desiredPositionRef?.current) {
      setDesiredTop(null)
      return
    }

    const rect = desiredPositionRef.current.getBoundingClientRect()
    setDesiredTop(rect.top)
  }, [desiredPositionRef])

  // Set up observers to watch for ref movement
  useLayoutEffect(() => {
    if (!desiredPositionRef?.current) {
      setDesiredTop(null)
      return
    }

    const element = desiredPositionRef.current

    updateDesiredPositionFromRef()

    const resizeObserver = new ResizeObserver(() => {
      updateDesiredPositionFromRef()
    })

    const interval = setInterval(updateDesiredPositionFromRef, 100)

    resizeObserver.observe(element)

    return () => {
      resizeObserver.disconnect()
      clearInterval(interval)
    }
  }, [desiredPositionRef?.current, updateDesiredPositionFromRef])

  const finalPositionTop = useMemo(() => {
    if (!desiredTop) {
      return 0
    }

    let candidateTop = desiredTop + topOffset

    if (candidateTop + bounds.height > viewportHeight - VIEWPORT_PADDING) {
      candidateTop = viewportHeight - bounds.height - VIEWPORT_PADDING
    }

    return Math.max(candidateTop, 0)
  }, [bounds.height, desiredTop, viewportHeight])

  return (
    <Box
      position="absolute"
      top={0}
      left={0}
      width="100%"
      height="100%"
      background="rgba(255, 255, 255, 0.5)"
      zIndex={1000}
      data-testid="invoice.lineItemEditor"
    >
      <Box ref={spaceRef} width="100%" height="100%">
        {spaceBounds.width > 0 && (
          <Flex
            width={`${spaceBounds.width}px`}
            position="fixed"
            top={`${finalPositionTop}px`}
            transition="top 0.2s ease-in-out, left 0.2s ease-in-out"
            justifyContent="center"
          >
            <Flex
              ref={ref}
              flexDir="column"
              maxWidth={CONTAINER_STYLES.maxWidth}
              width={`calc(100% + ${CONTAINER_STYLES.padding})`}
              paddingX={`${parseInt(CONTAINER_STYLES.padding) / 2}px`}
              height="fit-content"
              position="relative"
              maxHeight="calc(100vh - 40px)"
            >
              <Flex
                maxHeight="100%"
                overflowY="auto"
                height="100%"
                flexDir="column"
                background={GreyWhite}
                padding="16px"
                borderRadius={BorderRadius8}
                boxShadow={ShadowM}
                border={`${IndigoIndigo50} 1px solid`}
                gap="16px"
              >
                {children}
              </Flex>
            </Flex>
          </Flex>
        )}
      </Box>
    </Box>
  )
}

const useViewportHeight = () => {
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight)

  useLayoutEffect(() => {
    const handleResize = () => setViewportHeight(window.innerHeight)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return viewportHeight
}
